import getDisplayMonthRangeStart from './getDisplayMonthRangeStart';
import TimeConstants from 'owa-datetime-utils/lib/TimeConstants';
import { addDays, DisplayDate } from 'owa-datetime';
import { addDisplayMinutes } from 'owa-datetime-utils/lib/addDisplayMinutes';
import {
    DroppableType,
    calculateColumnIndexFromPoint,
    CalendarSurfaceStore,
    ColumnProvider,
} from 'owa-calendar-surface-dategrid';
import { DateRange, getWeeksInMonth } from 'owa-datetime-utils';

// TODO: We need to move this to the LayoutManager so we eliminate
// dependencies on TimeConstants, day size, hourIncrement and column count.

export default function calculateGridDateFromPoint(
    store: CalendarSurfaceStore,
    columnProvider: ColumnProvider,
    gridContainer: HTMLElement,
    droppableType: DroppableType,
    pageX: number,
    pageY: number
): DisplayDate {
    if (droppableType == DroppableType.Month) {
        return calculateMonthGridDate(store, gridContainer, pageX, pageY);
    } else {
        // Time grid
        return calculateTimeGridDate(
            store,
            columnProvider,
            gridContainer,
            droppableType,
            pageX,
            pageY
        );
    }
}

export function calculateGridTimeRangeFromPoint(
    store: CalendarSurfaceStore,
    columnProvider: ColumnProvider,
    gridContainer: HTMLElement,
    droppableType: DroppableType,
    pageX: number,
    pageY: number
): DateRange {
    let start = calculateTimeGridDate(
        store,
        columnProvider,
        gridContainer,
        droppableType,
        pageX,
        pageY
    );
    let end = addDisplayMinutes(start, store.userOptions.hourIncrement);
    return { start: start, end: end };
}

/**
 * Computes a date on the surface from a coordinate on the screen
 * @param store The calendar surface store
 * @param columnProvider The column provider
 * @param gridContainer The DOM element representing the regular surface time grid
 * @param droppableType The type of droppable, i.e., day/week, all-day
 * @param pageX The x position on screen
 * @param pageY The y position on screen
 */
export function calculateTimeGridDate(
    store: CalendarSurfaceStore,
    columnProvider: ColumnProvider,
    gridContainer: HTMLElement,
    droppableType: DroppableType,
    pageX: number,
    pageY: number
): DisplayDate {
    let boundingRect = gridContainer.getBoundingClientRect(); // this is wrong because it's including the week number well

    let timeIncrementInMinutes = store.userOptions.hourIncrement;
    let columnCount = columnProvider ? columnProvider.columnCount : 0;
    let columnIndex = calculateColumnIndexFromPoint(
        pageX,
        boundingRect,
        columnProvider,
        store.userOptions.isRTL
    );

    // Ensure column index is between 0 and columnIndex
    columnIndex = Math.max(0, columnIndex);
    columnIndex = Math.min(columnCount - 1, columnIndex);

    let calculatedDate = columnProvider.getColumnDate(columnIndex);

    // Calculate the time
    if (droppableType == DroppableType.DayWeek) {
        // Account for zoom factor since offsetWidth/offsetHeight are logical size in px
        let zoomFactor = boundingRect.width / gridContainer.offsetWidth;
        let gridTop = boundingRect.top - gridContainer.scrollTop;
        let percentScroll = (pageY - gridTop) / zoomFactor / gridContainer.scrollHeight;
        let percentScrollMinutes = percentScroll * TimeConstants.MinutesInOneDay;
        let snapMinuteSlot = Math.floor(percentScrollMinutes / timeIncrementInMinutes);
        let totalMinutes = snapMinuteSlot * timeIncrementInMinutes;

        // Since this is a visual operation, we need to add 'display' minutes,
        // not "real" minutes. With real minutes, adding 60 minutes to 1am on DST change
        // can put you back on another 1am (ex: PST Nov5, 1a(-7) + 60min => 1am(-8)).
        // With "display" minutes, we ensure 1am + 60 min is 2am, regardless of timezone.
        // In the example above, these 60 "display" minutes become 120 "real" minutes.
        // Note that we use addDisplayMinutes only for visual/user-initiated operations,
        // not for normal date-math.
        calculatedDate = addDisplayMinutes(calculatedDate, totalMinutes);
    }
    return calculatedDate;
}

function calculateMonthGridDate(
    store: CalendarSurfaceStore,
    gridContainer: HTMLElement,
    pageX: number,
    pageY: number
): DisplayDate {
    let gridBounds = gridContainer.getBoundingClientRect();
    let weekHeight =
        gridBounds.height / getWeeksInMonth(store.dateRange.start, store.userOptions.weekStartDay);
    let dayCount = TimeConstants.DaysInOneWeek;
    let dayWidth = gridBounds.width / dayCount;
    let dayOfWeekIndex = store.userOptions.isRTL
        ? Math.floor((dayWidth * dayCount - pageX + gridBounds.left) / dayWidth)
        : Math.floor((pageX - gridBounds.left) / dayWidth);

    let weekIndex = Math.floor((pageY - gridBounds.top) / weekHeight);
    let monthStartDay: DisplayDate = getDisplayMonthRangeStart(store);
    let daysToAdd = weekIndex * dayCount + dayOfWeekIndex;
    return addDays(monthStartDay, daysToAdd);
}
